/**
 * A apuração de uma denúncia pelo comitê.
 *
 * Mostra o conteúdo sigiloso, deixa mudar o status (com trilha), trocar mensagens com o relator e
 * — quando procedente — abrir a ação no Plano de Ação (o elo). Só chega aqui quem tem `denuncia.read`;
 * agir pede `denuncia.update`.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { PageShell } from '../../components';
import { useAcao } from '../../authorization/useVeredito';
import denunciaService from '../../services/denunciaService';
import {
  DenunciaDetalhe,
  DenunciaStatus,
  SITUACOES_DA_DENUNCIA,
  ROTULO_STATUS,
  ROTULO_CATEGORIA,
  COR_DO_STATUS,
} from '../../types/denuncia';

export const DenunciaDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const apurar = useAcao('denuncia.update', 'Apurar');

  const [detalhe, setDetalhe] = useState<DenunciaDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [novoStatus, setNovoStatus] = useState<DenunciaStatus | ''>('');
  const [texto, setTexto] = useState('');
  const [visivel, setVisivel] = useState(false);

  const carregar = useCallback(async () => {
    try {
      setDetalhe(await denunciaService.obter(Number(id)));
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar a denúncia.');
    }
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const mudarStatus = useCallback(async () => {
    if (!novoStatus) return;
    await denunciaService.mudarStatus(Number(id), novoStatus);
    setNovoStatus('');
    await carregar();
  }, [novoStatus, id, carregar]);

  const registrar = useCallback(async (tipo: 'NOTA' | 'MENSAGEM') => {
    if (!texto.trim()) return;
    await denunciaService.adicionarAndamento(Number(id), { tipo, texto: texto.trim(), visivelAoRelator: tipo === 'MENSAGEM' ? true : visivel });
    setTexto('');
    setVisivel(false);
    await carregar();
  }, [texto, visivel, id, carregar]);

  const gerarAcao = useCallback(async () => {
    await denunciaService.gerarAcaoNoPlano(Number(id));
    setAviso('Ação criada no Plano de Ação — a apuração vira uma medida com prazo e responsável.');
    await carregar();
  }, [id, carregar]);

  const baixarAnexo = useCallback(async (anexoId: number, nome: string) => {
    const blob = await denunciaService.baixarAnexo(Number(id), anexoId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    a.click();
    URL.revokeObjectURL(url);
  }, [id]);

  const anexarComite = useCallback(async (arquivo: File | undefined) => {
    if (!arquivo) return;
    await denunciaService.anexarPeloComite(Number(id), arquivo);
    await carregar();
  }, [id, carregar]);

  const d = detalhe?.denuncia;

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Canal de Denúncia', path: '/denuncias' }, { label: d?.protocolo ?? 'Denúncia' }]}
      title={d?.titulo ?? 'Denúncia'}
      subtitle={d ? `${ROTULO_CATEGORIA[d.categoria]} · protocolo ${d.protocolo}` : undefined}
    >
      {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}
      {aviso && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setAviso(null)}
          action={<Button color="inherit" size="small" onClick={() => navigate('/action-plan')}>Ver no plano</Button>}
        >
          {aviso}
        </Alert>
      )}

      {d && (
        <Stack spacing={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip color={COR_DO_STATUS[d.status]} label={ROTULO_STATUS[d.status]} />
              {d.anonima ? <Chip size="small" variant="outlined" label="Anônima" /> : (
                <Typography variant="body2" color="text.secondary">{d.relatorNome}</Typography>
              )}
            </Stack>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{d.descricao}</Typography>
            {d.local && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Local: {d.local}</Typography>}
            {d.dataDoOcorrido && <Typography variant="body2" color="text.secondary">Ocorrido em: {new Date(d.dataDoOcorrido).toLocaleDateString('pt-BR')}</Typography>}
            {d.envolvidos && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Envolvidos: {d.envolvidos}</Typography>}
          </Paper>

          {!apurar.disabled && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Apuração</Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
                <Select size="small" value={novoStatus} displayEmpty sx={{ minWidth: 180 }}
                  onChange={(e) => setNovoStatus(e.target.value as DenunciaStatus)}>
                  <MenuItem value="">Mudar situação…</MenuItem>
                  {SITUACOES_DA_DENUNCIA.map((s) => <MenuItem key={s} value={s}>{ROTULO_STATUS[s]}</MenuItem>)}
                </Select>
                <Button variant="contained" onClick={() => void mudarStatus()} disabled={!novoStatus}>Aplicar</Button>
                {d.actionPlanItemId ? (
                  <Button variant="outlined" color="secondary" onClick={() => navigate('/action-plan')}>
                    Ver a ação no plano
                  </Button>
                ) : d.status === 'PROCEDENTE' ? (
                  <Button variant="outlined" color="secondary" onClick={() => void gerarAcao()}>
                    Abrir ação no Plano de Ação
                  </Button>
                ) : null}
              </Stack>
              <TextField label="Nota interna ou mensagem ao relator" multiline minRows={2} fullWidth
                value={texto} onChange={(e) => setTexto(e.target.value)} sx={{ mb: 1 }} />
              <FormControlLabel
                control={<Checkbox checked={visivel} onChange={(e) => setVisivel(e.target.checked)} />}
                label="Visível ao relator"
              />
              <Stack direction="row" spacing={1}>
                <Button onClick={() => void registrar('NOTA')} disabled={!texto.trim()}>Registrar nota</Button>
                <Button variant="contained" onClick={() => void registrar('MENSAGEM')} disabled={!texto.trim()}>Responder ao relator</Button>
              </Stack>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Provas anexadas</Typography>
              {!apurar.disabled && (
                <Button size="small" variant="outlined" component="label">
                  Anexar
                  <input hidden type="file" accept=".pdf,image/*"
                    onChange={(e) => void anexarComite(e.target.files?.[0])} />
                </Button>
              )}
            </Stack>
            {detalhe.anexos.length === 0 ? (
              <Typography color="text.secondary" variant="body2">Nenhuma prova anexada.</Typography>
            ) : (
              <Stack spacing={0.5}>
                {detalhe.anexos.map((anexo) => (
                  <Stack key={anexo.id} direction="row" spacing={1} alignItems="center">
                    <Button size="small" onClick={() => void baixarAnexo(anexo.id, anexo.originalName ?? `anexo-${anexo.id}`)}>
                      {anexo.originalName ?? `anexo-${anexo.id}`}
                    </Button>
                    <Chip size="small" variant="outlined" label={anexo.origem === 'RELATOR' ? 'Relator' : 'Comitê'} />
                  </Stack>
                ))}
              </Stack>
            )}
          </Paper>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Histórico da apuração</Typography>
            {detalhe.andamentos.length === 0 ? (
              <Typography color="text.secondary">Sem andamentos ainda.</Typography>
            ) : (
              <Stack spacing={1}>
                {detalhe.andamentos.map((a) => (
                  <Paper key={a.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        {a.origem === 'RELATOR' ? 'Relator' : a.origem === 'SISTEMA' ? 'Sistema' : 'Comitê'} · {new Date(a.createdAt).toLocaleString('pt-BR')}
                      </Typography>
                      {a.tipo === 'STATUS' && a.statusNovo && (
                        <Chip size="small" color={COR_DO_STATUS[a.statusNovo]} label={ROTULO_STATUS[a.statusNovo]} />
                      )}
                      {!a.visivelAoRelator && <Chip size="small" variant="outlined" label="Interno" />}
                    </Stack>
                    {a.texto && <Typography sx={{ mt: 0.5 }}>{a.texto}</Typography>}
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </PageShell>
  );
};

export default DenunciaDetailPage;
